import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "./DetailElegentParatha.css";

/* ------------------------------------------------------------------
   COLLECTIONS (static)
   Key (slug) = wahi jo HomeSection1 ke card ke link mein jayega:
   /elegant-collection/banarasi
   - title : page ka heading
   - fabric: product ke naam mein lagta hai
   - count : kitne products dikhane hain (abhi placeholder data banta hai)

   Products abhi automatic placeholder data se ban rahe hain
   (tumhari /img/scrollsec*.webp images use hoti hain).
   Asli products dalne ke liye neeche "buildProducts" ki jagah apni
   list likh dena, har product ka format:
   { id, name, price, oldPrice, images: ["/img/a.webp", "/img/b.webp"], link }
------------------------------------------------------------------- */
const COLLECTIONS = {
  banarasi: { title: "Banarasi", fabric: "Banarasi Silk", count: 12 },
  "kota-cotton": { title: "Kota Cotton", fabric: "Kota Cotton", count: 8 },
  mulmul: { title: "Mulmul", fabric: "Mulmul Cotton", count: 10 },
  cotton: { title: "Cotton", fabric: "Cotton", count: 12 },
  "maheshwari-silk": { title: "Maheshwari Silk", fabric: "Maheshwari Silk", count: 8 },
  linen: { title: "Linen", fabric: "Linen", count: 8 },
  tissue: { title: "Tissue", fabric: "Tissue Silk", count: 6 },
  "dual-shade": { title: "Dual Shade", fabric: "Dual Shade Silk", count: 8 },
};

const NAME_POOL = [
  "Kanak Jaal",
  "Shabnam",
  "Dhoop Rani",
  "Sunehri Chauka",
  "Gulmohar",
  "Nazrana",
  "Ruhira",
  "Gulrang",
  "Neelam",
  "Kesariya",
  "Gulabi Zari",
  "Hara Bhara",
];

/* Placeholder products banata hai. Asli data aane par isko hata dena */
const buildProducts = (slug, fabric, count) =>
  Array.from({ length: count }, (_, i) => {
    const price = Math.round((1900 + ((i * 437) % 2500)) / 50) * 50 - 1;
    const oldPrice = Math.round((price * 1.45) / 50) * 50 - 1;
    const a = ((i * 2) % 10) + 1;
    const b = ((i * 2 + 1) % 10) + 1;

    return {
      id: `${slug}-${i + 1}`,
      name: `${NAME_POOL[i % NAME_POOL.length]} ${fabric} Saree`,
      price,
      oldPrice,
      images: [`/img/scrollsec${a}.webp`, `/img/scrollsec${b}.webp`],
      // Detail page abhi sirf ln-1 se ln-5 ke liye bana hai, isliye inhi mein ghuma rahe hain
      link: `/launch-product/ln-${(i % 5) + 1}`,
    };
  });

/* Collection ke upar heading dikhani hai ya nahi (screenshot mein nahi thi) */
const SHOW_TITLE = true;

/* ---------------- SORT OPTIONS ---------------- */
const SORT_OPTIONS = [
  { key: "featured", label: "Featured" },
  { key: "price-asc", label: "Price, low to high" },
  { key: "price-desc", label: "Price, high to low" },
  { key: "name-asc", label: "Alphabetically, A-Z" },
  { key: "name-desc", label: "Alphabetically, Z-A" },
];

/* ---------------- FILTER: PRICE RANGES ---------------- */
const PRICE_RANGES = [
  { key: "under-2500", label: "Under Rs. 2,500", min: 0, max: 2500 },
  { key: "2500-3500", label: "Rs. 2,500 - Rs. 3,500", min: 2500, max: 3500 },
  { key: "3500-4500", label: "Rs. 3,500 - Rs. 4,500", min: 3500, max: 4500 },
  { key: "above-4500", label: "Above Rs. 4,500", min: 4500, max: Infinity },
];

const formatPrice = (value) =>
  `Rs. ${Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/* ---------------------------- ICONS ---------------------------- */
const ChevronDown = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
    <path d="M5 5l14 14M19 5L5 19" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

/* --------------------------- COMPONENT -------------------------- */
const DetailElegentParatha = () => {
  const { slug } = useParams();
  const collection = COLLECTIONS[slug];

  const [sortKey, setSortKey] = useState("featured");
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedRanges, setSelectedRanges] = useState([]);

  const sortRef = useRef(null);

  /* Collection badalne par sab reset + page upar se */
  useEffect(() => {
    window.scrollTo(0, 0);
    setSortKey("featured");
    setSortOpen(false);
    setFilterOpen(false);
    setSelectedRanges([]);
  }, [slug]);

  /* Sort dropdown: bahar click / Esc se band */
  useEffect(() => {
    const onClick = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSortOpen(false);
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  /* Filter drawer khulne par body scroll lock */
  useEffect(() => {
    if (!filterOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [filterOpen]);

  const allProducts = useMemo(
    () => (collection ? buildProducts(slug, collection.fabric, collection.count) : []),
    [slug, collection]
  );

  /* Filter + sort */
  const visibleProducts = useMemo(() => {
    let list = [...allProducts];

    if (selectedRanges.length > 0) {
      const ranges = PRICE_RANGES.filter((r) => selectedRanges.includes(r.key));
      list = list.filter((p) =>
        ranges.some((r) => p.price >= r.min && p.price < r.max)
      );
    }

    switch (sortKey) {
      case "price-asc":
        list.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        list.sort((a, b) => b.price - a.price);
        break;
      case "name-asc":
        list.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-desc":
        list.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        break;
    }

    return list;
  }, [allProducts, selectedRanges, sortKey]);

  const toggleRange = (key) => {
    setSelectedRanges((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const count = visibleProducts.length;
  const countLabel = `${count} ${count === 1 ? "product" : "products"}`;

  if (!collection) {
    return (
      <section className="detailelegentparatha-section">
        <div className="detailelegentparatha-notfound">
          <h2>Collection not found</h2>
          <Link to="/" className="detailelegentparatha-back">
            Back to home
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="detailelegentparatha-section">
      {SHOW_TITLE && (
        <h1 className="detailelegentparatha-title">{collection.title}</h1>
      )}

      {/* ---------------- TOP BAR: count / sort / filter ---------------- */}
      <div className="detailelegentparatha-bar">
        <div className="detailelegentparatha-bar-side" />

        <p className="detailelegentparatha-count" aria-live="polite">
          {countLabel}
        </p>

        <div className="detailelegentparatha-actions">
          {/* Sort by */}
          <div className="detailelegentparatha-sort" ref={sortRef}>
            <button
              type="button"
              className="detailelegentparatha-action-btn"
              aria-haspopup="listbox"
              aria-expanded={sortOpen}
              onClick={() => setSortOpen((p) => !p)}
            >
              <span>Sort by</span>
              <span
                className={`detailelegentparatha-chevron ${
                  sortOpen ? "detailelegentparatha-chevron-open" : ""
                }`}
              >
                <ChevronDown />
              </span>
            </button>

            <ul
              className={`detailelegentparatha-sort-menu ${
                sortOpen ? "detailelegentparatha-open" : ""
              }`}
              role="listbox"
            >
              {SORT_OPTIONS.map((opt) => (
                <li key={opt.key}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={sortKey === opt.key}
                    className={`detailelegentparatha-sort-option ${
                      sortKey === opt.key ? "detailelegentparatha-selected" : ""
                    }`}
                    onClick={() => {
                      setSortKey(opt.key);
                      setSortOpen(false);
                    }}
                  >
                    {opt.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Filter */}
          <button
            type="button"
            className="detailelegentparatha-action-btn detailelegentparatha-filter-btn"
            onClick={() => setFilterOpen(true)}
          >
            <span>Filter</span>
            {selectedRanges.length > 0 && (
              <span className="detailelegentparatha-filter-count">
                {selectedRanges.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ---------------- GRID ---------------- */}
      {count === 0 ? (
        <div className="detailelegentparatha-empty">
          <p>No products match your filters.</p>
          <button
            type="button"
            className="detailelegentparatha-back"
            onClick={() => setSelectedRanges([])}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <div className="detailelegentparatha-grid">
          {visibleProducts.map((product, index) => (
            <article
              className="detailelegentparatha-card"
              key={product.id}
              style={{ "--detailelegentparatha-delay": `${(index % 4) * 70}ms` }}
            >
              <Link
                to={product.link}
                className="detailelegentparatha-media"
                aria-label={product.name}
              >
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="detailelegentparatha-img detailelegentparatha-img-main"
                  loading="lazy"
                  draggable={false}
                />
                {product.images[1] && (
                  <img
                    src={product.images[1]}
                    alt=""
                    className="detailelegentparatha-img detailelegentparatha-img-hover"
                    loading="lazy"
                    draggable={false}
                  />
                )}
              </Link>

              <Link to={product.link} className="detailelegentparatha-info">
                <h3 className="detailelegentparatha-name">{product.name}</h3>
                <p className="detailelegentparatha-price">
                  <span>{formatPrice(product.price)}</span>
                  <s>{formatPrice(product.oldPrice)}</s>
                </p>
              </Link>
            </article>
          ))}
        </div>
      )}

      {/* ---------------- FILTER DRAWER ---------------- */}
      <div
        className={`detailelegentparatha-overlay ${
          filterOpen ? "detailelegentparatha-open" : ""
        }`}
        onClick={() => setFilterOpen(false)}
        aria-hidden={!filterOpen}
      />

      <aside
        className={`detailelegentparatha-drawer ${
          filterOpen ? "detailelegentparatha-open" : ""
        }`}
        aria-label="Filter"
        aria-hidden={!filterOpen}
      >
        <div className="detailelegentparatha-drawer-head">
          <h4>Filter</h4>
          <button
            type="button"
            className="detailelegentparatha-drawer-close"
            aria-label="Close filter"
            onClick={() => setFilterOpen(false)}
            tabIndex={filterOpen ? 0 : -1}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="detailelegentparatha-drawer-body">
          <h5 className="detailelegentparatha-filter-title">Price</h5>

          <ul className="detailelegentparatha-range-list">
            {PRICE_RANGES.map((range) => {
              const checked = selectedRanges.includes(range.key);
              return (
                <li key={range.key}>
                  <label className="detailelegentparatha-range">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRange(range.key)}
                      tabIndex={filterOpen ? 0 : -1}
                    />
                    <span className="detailelegentparatha-box">
                      <CheckIcon />
                    </span>
                    <span>{range.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="detailelegentparatha-drawer-foot">
          <button
            type="button"
            className="detailelegentparatha-clear"
            onClick={() => setSelectedRanges([])}
            disabled={selectedRanges.length === 0}
            tabIndex={filterOpen ? 0 : -1}
          >
            Clear all
          </button>

          <button
            type="button"
            className="detailelegentparatha-apply"
            onClick={() => setFilterOpen(false)}
            tabIndex={filterOpen ? 0 : -1}
          >
            Show {countLabel}
          </button>
        </div>
      </aside>
    </section>
  );
};

export default DetailElegentParatha;